import apiClient from './apiClient';

// Interface for fetching freelance profile
export interface GetFreelanceProfile {
	id: string;
	firstName: string;
	lastName: string;
	email: string;
	hourlyRate: number;
	availableFrom: string;
	isVisibleOnline: boolean;
	location: string;
	currentTitle: string;
	bioHtml: string;
	whatLookingFor: string;
	personalStatement: string;
	skills: string[];
	tags: string[];
	legalName: string;
	vatNumber: string;
	fiscalCode: string;
	italianSdiCode: string;
	billingAddress: string;
	billingCity: string;
	billingProvince: string;
	billingZipCode: string;
	billingCountryCode: string;
	billingRegime: number;
	billingEmail: string;
	certifiedEmail: string;
	iban: string;
	phoneNumber?: string;
}

// Interface for updating freelance profile (make fields optional to support partial updates)
export interface UpdateFreelanceProfile {
	firstName?: string;
	lastName?: string;
	email?: string;
	phoneNumber?: string;
	currentTitle?: string;
	hourlyRate?: number;
	availableFrom?: string;
	isVisibleOnline?: boolean;
	location?: string;
	bioHtml?: string;
	skills?: string[];
	tags?: string[];
	whatLookingFor?: string;
	personalStatement?: string;
	legalName?: string;
	vatNumber?: string;
	fiscalCode?: string;
	italianSdiCode?: string;
	vatPercentage?: number;
	billingAddress?: string;
	billingCity?: string;
	billingProvince?: string;
	billingZipCode?: string;
	billingCountryCode?: string;
	billingEmail?: string;
	billingRegime?: string;
	certifiedEmail?: string;
	iban?: string;
}

const profileService = {
	/**
	 * Fetches the freelance user profile from the backend
	 */
	async getFreelanceProfile(): Promise<GetFreelanceProfile> {
		try {
			const response = await apiClient.get<GetFreelanceProfile>('/api/talentusers/profile');
			return response.data;
		} catch (error: any) {
			const errorMessage = error.response?.data?.message || 'Failed to fetch profile data';
			throw new Error(errorMessage);
		}
	},

	/**
	 * Updates the freelance user profile
	 */
	async updateFreelanceProfile(payload: UpdateFreelanceProfile): Promise<GetFreelanceProfile> {
		try {
			const response = await apiClient.put<GetFreelanceProfile>(
				'/api/talentusers/profile',
				payload,
			);
			return response.data;
		} catch (error: any) {
			const errorMessage = error.response?.data?.message || 'Failed to update profile';
			throw new Error(errorMessage);
		}
	},

	/**
	 * Uploads a CV to the freelance user profile
	 */
	async uploadCV(file: File): Promise<GetFreelanceProfile> {
		try {
			const formData = new FormData();
			formData.append('file', file);
			const response = await apiClient.post<GetFreelanceProfile>(
				'/api/talentusers/profile/cv',
				formData,
				{
					headers: {
						'Content-Type': 'multipart/form-data',
					},
				},
			);
			return response.data;
		} catch (error: any) {
			const errorMessage = error.response?.data?.message || 'Failed to upload CV';
			throw new Error(errorMessage);
		}
	},
};

export default profileService;
